import React, { useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, ToggleButton, ToggleButtonGroup, CircularProgress, Typography } from "@mui/material";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import DescriptionIcon from "@mui/icons-material/Description";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import { requestOptions, HttpMethod } from "../../utils/api/request-options";
import { isValidHttpUrl } from "../../utils/helper/string-helper";

export default function PresentationViewer({
    badgeName,
    presentationUrl,
    sessionMapUrl,
    submissionUrl,
    showSubmission = false,
    showSessionMap = true
}: {
    badgeName: string,
    presentationUrl?: string,
    sessionMapUrl?: string,
    submissionUrl?: string,
    showSubmission?: boolean,
    showSessionMap?: boolean
}) {
    const isDevMode = process.env.NODE_ENV !== "production";

    const sanitizeUrl = (url?: string) => {
        const trimmed = url?.trim();
        if (!trimmed || !isValidHttpUrl(trimmed)) {
            return undefined;
        }
        return trimmed;
    };

    const safePresentationUrl = sanitizeUrl(presentationUrl);
    const safeSessionMapUrl = showSessionMap ? sanitizeUrl(sessionMapUrl) : undefined;
    const safeSubmissionUrl = showSubmission ? sanitizeUrl(submissionUrl) : undefined;

    const availableTypes = [
        safePresentationUrl ? "presentation" : undefined,
        safeSessionMapUrl ? "sessionMap" : undefined,
        safeSubmissionUrl ? "submission" : undefined
    ].filter(Boolean) as Array<'presentation' | 'sessionMap' | 'submission'>;

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [viewType, setViewType] = useState<'presentation' | 'sessionMap' | 'submission'>(availableTypes[0] ?? 'presentation');

    if (!safePresentationUrl && !safeSessionMapUrl && !safeSubmissionUrl) return null;

    const currentOriginalUrl =
        viewType === 'presentation' ? safePresentationUrl :
        viewType === 'sessionMap' ? safeSessionMapUrl :
        safeSubmissionUrl;

    const loadPdf = (type: 'presentation' | 'sessionMap' | 'submission') => {
        const url =
            type === 'presentation' ? safePresentationUrl :
            type === 'sessionMap' ? safeSessionMapUrl :
            safeSubmissionUrl;
        if (!url) return;

        setViewType(type);
        setLoading(true);
        setError(null);
        setPdfUrl(null);

        fetch(`${process.env.REACT_APP_LMS_API_URL}/v3/drive/pdf?url=${encodeURIComponent(url)}`, requestOptions(HttpMethod.GET))
            .then(async (response) => {
                if (!response.ok) {
                    return response.text().then(text => { throw new Error(text); });
                }

                const bytes = new Uint8Array(await response.arrayBuffer());
                if (bytes.length === 0) {
                    throw new Error("Drive proxy returned an empty file");
                }

                const isPdf = bytes.length >= 5
                    && bytes[0] === 0x25
                    && bytes[1] === 0x50
                    && bytes[2] === 0x44
                    && bytes[3] === 0x46
                    && bytes[4] === 0x2D;

                if (!isPdf) {
                    const preview = new TextDecoder().decode(bytes.slice(0, 240));
                    const contentType = response.headers.get("content-type") ?? "unknown";
                    throw new Error(
                        `Expected a PDF but received different content (content-type: ${contentType}). ` +
                        `Response preview: ${preview}`
                    );
                }

                return new Blob([bytes], { type: "application/pdf" });
            })
            .then(blob => {
                const objectUrl = URL.createObjectURL(blob);
                setPdfUrl(objectUrl);
                setLoading(false);
            })
            .catch(err => {
                setError(err.message || "Failed to load file");
                setLoading(false);
            });
    };

    const getFriendlyErrorMessage = (rawError?: string | null) => {
        if (!rawError) {
            return "This resource cannot be previewed inside LMS right now.";
        }

        const normalizedError = rawError.toLowerCase();

        if (normalizedError.includes("cannot convert this file type to pdf")) {
            return "This file type cannot be previewed inside LMS. Please open the original file in a new tab.";
        }

        if (normalizedError.includes("forbidden") || normalizedError.includes("permission")) {
            return "LMS does not have permission to preview this file. Please open the original file in a new tab.";
        }

        if (normalizedError.includes("not found")) {
            return "The file could not be found for preview. Please open the original file in a new tab.";
        }

        if (normalizedError.includes("empty file") || normalizedError.includes("empty stream")) {
            return "This file could not be previewed inside LMS. Please open the original file in a new tab.";
        }

        return "This file cannot be previewed inside LMS right now. You can open the original file in a new tab.";
    };

    const handleOpen = (type: 'presentation' | 'sessionMap' | 'submission') => {
        setOpen(true);
        loadPdf(type);
    };

    const handleClose = () => {
        setOpen(false);
        if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }
    };

    const switchView = (type: 'presentation' | 'sessionMap' | 'submission') => {
        if (type !== viewType) {
            loadPdf(type);
        }
    };

    return (
        <>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                {safePresentationUrl && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SlideshowIcon />}
                        onClick={() => handleOpen('presentation')}
                    >
                        View Presentation
                    </Button>
                )}
                {safeSessionMapUrl && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DescriptionIcon />}
                        onClick={() => handleOpen('sessionMap')}
                    >
                        View Session Map
                    </Button>
                )}
                {safeSubmissionUrl && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => handleOpen('submission')}
                    >
                        View Student Submission
                    </Button>
                )}
            </Box>
            <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" noWrap>{badgeName}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {availableTypes.length > 1 && (
                            <ToggleButtonGroup
                                size="small"
                                value={viewType}
                                exclusive
                                onChange={(_, v) => { if (v) switchView(v); }}
                            >
                                {safePresentationUrl && <ToggleButton value="presentation"><SlideshowIcon fontSize="small" sx={{ mr: 0.5 }} /> Slides</ToggleButton>}
                                {safeSessionMapUrl && <ToggleButton value="sessionMap"><DescriptionIcon fontSize="small" sx={{ mr: 0.5 }} /> Session Map</ToggleButton>}
                                {safeSubmissionUrl && <ToggleButton value="submission"><OpenInNewIcon fontSize="small" sx={{ mr: 0.5 }} /> Submission</ToggleButton>}
                            </ToggleButtonGroup>
                        )}
                        <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0, height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {loading && <CircularProgress />}
                    {error && (
                        <Box sx={{ textAlign: 'center', p: 3 }}>
                            <Typography color="error" gutterBottom>Unable to load file</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {getFriendlyErrorMessage(error)}
                            </Typography>
                            {isDevMode && (
                                <Typography variant="body2" color="text.secondary">{error}</Typography>
                            )}
                            {currentOriginalUrl && (
                                <Button
                                    sx={{ mt: 2 }}
                                    variant="contained"
                                    href={currentOriginalUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    startIcon={<OpenInNewIcon />}
                                >
                                    Open Original File
                                </Button>
                            )}
                        </Box>
                    )}
                    {pdfUrl && !loading && (
                        <iframe
                            src={pdfUrl}
                            style={{ width: '100%', height: '100%', border: 'none' }}
                            title={`${badgeName} ${viewType}`}
                        />
                    )}
                </DialogContent>
                {currentOriginalUrl && (
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button
                            href={currentOriginalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<OpenInNewIcon />}
                        >
                            Open Original File
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
        </>
    );
}
